import React, { forwardRef, InputHTMLAttributes } from 'react';

import clsx from 'clsx';

import { Flex } from '../Flex';
import { IconCheckLine, IconMinusLine } from '../Icon';
import { Text } from '../Text';
import { checkboxWrapper, hiddenInput, checkBoxIcon, checkBoxIconWrapper } from './Checkbox.css';
import { useCheckboxGroup } from './CheckboxGroup';

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: React.ReactNode;
  indeterminate?: boolean;
  value?: string;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  (
    { label, checked, disabled, indeterminate = false, className, onChange, value, ...props },
    ref,
  ) => {
    const group = useCheckboxGroup();

    // Group context가 있으면 group의 값을 사용
    const isChecked = group ? value !== undefined && group.value?.includes(value) : checked;
    const isDisabled = group?.disabled || disabled;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (group?.onChange && value !== undefined) {
        const newValue = e.target.checked
          ? [...(group.value || []), value]
          : (group.value || []).filter((v) => v !== value);
        group.onChange(newValue);
      }
      onChange?.(e);
    };

    const displayChecked = indeterminate || isChecked || false;

    return (
      <label className={clsx(checkboxWrapper, className)}>
        <input
          ref={ref}
          type="checkbox"
          className={hiddenInput}
          checked={isChecked}
          disabled={isDisabled}
          onChange={handleChange}
          value={value}
          aria-checked={indeterminate ? 'mixed' : isChecked ? 'true' : 'false'}
          {...props}
        />
        <Flex
          align="center"
          justify="center"
          className={checkBoxIconWrapper({ disabled: isDisabled || false })}
        >
          <div
            className={checkBoxIcon({
              checked: displayChecked,
              disabled: isDisabled || false,
            })}
          >
            {indeterminate ? <IconMinusLine /> : isChecked && <IconCheckLine />}
          </div>
        </Flex>
        {label &&
          (typeof label === 'string' ? (
            <Text textStyle="14_400" color="semanticPrimary">
              {label}
            </Text>
          ) : (
            label
          ))}
      </label>
    );
  },
);

Checkbox.displayName = 'Checkbox';
